/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { ButtonComponent,
    CodeSnippetComponent, ScrollContainerComponent,
    LinkPanelComponent, ModalComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent, FormPasswordInputComponent, requiredValidator, FormComponent, FormActionsComponent, ModalCloseButtonComponent, FormInputComponent
} from "typedb-cloud-framework";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { SnackbarService } from "../../../../../service/snackbar.service";
import { Subscription, combineLatest, filter, first, map, shareReplay, takeUntil } from "rxjs";
import { MatSelectModule } from "@angular/material/select";
import { MatStepperModule } from "@angular/material/stepper";
import {
    buildConnectionString,
    clusterCredentialFilename, ConnectDialogData, copy,
    detectOS,
    OperatingSystem,
    parseVersionString
} from "../../shared";
import { ClusterStatus } from "../../../../../concept/cluster";
import { ClusterLoadingTipsComponent } from "../loading/cluster-loading-tips.component";

@Component({
    selector: "tp-cluster-studio-connect-dialog",
    templateUrl: "./cluster-studio-connect-dialog.component.html",
    styleUrls: ["./cluster-studio-connect-dialog.component.scss"],
    imports: [
        AsyncPipe, MatTooltipModule, MatButtonModule, MatSelectModule, ButtonComponent,
        CodeSnippetComponent, ScrollContainerComponent, MatStepperModule,
        MatDialogContent, MatDialogTitle, ModalCloseButtonComponent, ClusterLoadingTipsComponent
    ]
})
export class ClusterStudioConnectDialogComponent {
    serverAddresses$ = combineLatest([this.data.servers$, this.data.cluster$]).pipe(
        filter(([servers, cluster]) => servers.every((server) => !!server.address) && servers.length == cluster.serverCount),
        map(([servers, _]) => servers.map((server) => server.address!)),
        shareReplay(1),
    );
    clusterStatus$ = this.data.cluster$.pipe(map((cluster) => cluster.status));
    ready$ = this.clusterStatus$.pipe(map((status) => status == "running"));

    private loadingStatuses: ClusterStatus[] = ["starting", "resuming"];
    loading$ = this.clusterStatus$.pipe(map((status) => this.loadingStatuses.includes(status)));

    parsedVersion$ = this.data.cluster$.pipe(map(cluster => parseVersionString(cluster.typeDBVersion.version)));
    isTypeDBVersion2$ = this.parsedVersion$.pipe(map(version => version.major == 2));
    isTypeDBVersion3_2OrLater$ = this.parsedVersion$.pipe(map(version => version.major >= 3 && version.minor >= 2));

    docsVersionSpecifier$ = this.isTypeDBVersion2$.pipe(map((isVersion2) => isVersion2 ? "2.x/" : ""));
    studioInstallDocsLink$ = this.docsVersionSpecifier$.pipe(map((specifier) => `https://typedb.com/docs/home/${specifier}install-tools#_studio`));
    operatingSystem: OperatingSystem = detectOS();
    private studioLinkStart = `https://repo.typedb.com/public/public-release/raw/names/typedb-studio-${this.operatingSystem}/versions`;
    studioDownloadLink$ = this.isTypeDBVersion2$.pipe(map((isVersion2) =>
        isVersion2 ?
            `${this.studioLinkStart}/2.28.6/typedb-studio-${this.operatingSystem}-2.28.6.dmg` :
            `${this.studioLinkStart}/latest/download`
    ));

    connectionStringSnippet$ = combineLatest([this.serverAddresses$, this.parsedVersion$, this.data.cluster$]).pipe(
        map(([addresses, parsedVersion, cluster]) => ({
            code: buildConnectionString(undefined, addresses, parsedVersion, cluster.id).replaceAll('<', '&lt;').replaceAll('>', '&gt;')
        })),
        shareReplay(1),
    );
    readyStudioLink$ = combineLatest([this.isTypeDBVersion3_2OrLater$, this.ready$, this.serverAddresses$, this.data.cluster$]).pipe(
        map(([isTypeDBVersion3_2OrLater, ready, addresses, cluster]) => {
            if (isTypeDBVersion3_2OrLater && ready && cluster) return `https://studio.typedb.com/connect?username=admin&address=${addresses[0]}&name=${cluster.id}`;
            else return undefined;
        }),
    );

    constructor(
        private dialogRef: MatDialogRef<ClusterStudioConnectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConnectDialogData, protected snackbar: SnackbarService,
    ) {
        this.readyStudioLink$
            .pipe(takeUntil(this.dialogRef.beforeClosed()), first(link => !!link))
            .subscribe((link) => window.open(link, "blank"));
    }

    close() {
        this.dialogRef.close();
    }

    protected readonly OperatingSystem = OperatingSystem;
    protected readonly clusterCredentialFilename = clusterCredentialFilename;
    protected readonly copy = copy;
}
